import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { mediaSrc } from "../../api/mediaUrl"; // single tolerant image-src resolver
import { fileBlockedDomainAppeal } from "../../api/newsApi";
import { formatCommunityPathLabel } from "../../Data/ProjectConfig/projectConfigOverrides";
import styles from "./ContentApprovalsPage.module.css";

import DataTab from "./DataTab";
import TasksTab from "./TasksTab";
import ActionCenterInbox from "./ActionCenterInbox";
import { isActionCenterInboxEnabled } from "./actionCenterInboxFlag";
import { classifyInstanceLateness } from "../../Data/DataReliability/perInstanceState";
import {
  getArchetypeConfig,
  listIncompleteMilestones,
} from "../../Data/Onboarding/archetypeConfig";
import { evaluateMilestones } from "../../Data/Onboarding/milestoneEvaluator";
import type { WorkflowRequest, WorkflowActionType } from "../../workflow";
import {
  approveRequest,
  cancelRequest,
  rejectRequest,
  submitRequest,
  addOrUpdateWorkflow,
  getAllWorkflows,
  subscribeWorkflows,
  applyApprovedWorkflowSideEffects,
  applyRejectedWorkflowSideEffects,
  applyCancelledWorkflowSideEffects,
} from "../../workflow";

type Props = {
  currentUserGuid: string;
  debugMode?: boolean;
  users?: Record<string, any>[];
  projects?: Record<string, any>[];
  communities?: Record<string, any>[];
  /**
   * DEV-892. Community parent/child edges. Used to disambiguate org-typed
   * entity names by walking up to the org root — e.g. "Pawtucket" gets
   * rendered as "Pawtucket Rotary Club (Rotary International)" so the
   * recipient can tell which Pawtucket the invitation refers to (the
   * geo_city or the org_rotary_club).
   */
  relationships?: Record<string, any>[];
  /**
   * DEV-681 Phase 2 — per-project rows (the same `Data` array App.tsx
   * routes to other surfaces). Each row carries Action Center inputs:
   * data_reliability_expected_instances, schedule/finance instances,
   * latest points by instance, can_edit_kpi flag, mission_subkpis.
   */
  data?: any[];
  /** Bumps projectConfigVersion in App so post-save re-fetch triggers. */
  onProjectConfigChanged?: () => void;
  /**
   * DEV-681 Phase 3 — inputs for the synthetic milestone evaluator
   * (Path C). When DEV-508's runtime evaluator service ships, these
   * become unused but the consumer signature stays stable.
   */
  communityMemberships?: Record<string, any>[];
  projectAcl?: Record<string, any>[];
  /**
   * SPEC_ROLE_ORGANIZED_INBOX §4a — threaded through to the Action Center rail's
   * useEngagementEntries (project hero photos + community role assignments) so
   * the role cards match the Engagements page. Optional; degrade gracefully.
   */
  allProjectHeroLookup?: Map<string, string>;
  communityRoleAssignments?: Record<string, any>[];
};

/* ── Item-type categories for filter bar ──────────────────────────── */

type ItemCategory = "all" | "people" | "reviews" | "governance" | "notifications" | "data";

/**
 * Sub-type within "people" category to distinguish invitations from access requests.
 * - "invitation": org/authority invites a person (created_by ≠ subject)
 * - "access": person requests access for themselves (created_by = subject)
 */
type PeopleSubType = "invitation" | "access";

/** Determine if a people-related workflow is an invitation or access request. */
function getPeopleSubType(w: WorkflowRequest): PeopleSubType {
  const p = w.payload || {};
  const subjectGuid = String(p.user_guid || p.target_user_guid || "");
  const createdBy = String(w.created_by || "");
  if (subjectGuid && createdBy && subjectGuid.toUpperCase() === createdBy.toUpperCase()) {
    return "access"; // person requesting for themselves
  }
  return "invitation"; // authority acting on someone's behalf
}

/** Map workflow action_type (+ payload context) → category for filtering. */
function categorize(actionType: WorkflowActionType | string, w?: WorkflowRequest): ItemCategory {
  // Role assignments, permissions changes, and DEV-1030 acknowledgment
  // notifications all route to the People category which lands in the Inbox.
  if (
    actionType === "project.role_assignment" ||
    actionType === "community.role_assignment" ||
    actionType === "mini_project.role_assignment" ||
    actionType === "mini_project.role_accepted" ||
    actionType === "mini_project.role_declined" ||
    actionType === "mini_project.role_resignation" ||
    actionType === "mini_project.role_revoked" ||
    actionType === "project.permissions_change" ||
    actionType === "community.permissions_change" ||
    actionType === "project.role_resignation" ||
    actionType === "project.role_revoked" ||
    actionType === "project.role_declined" ||
    actionType === "project.role_accepted" ||
    actionType === "community.role_resignation" ||
    actionType === "community.role_revoked" ||
    actionType === "community.role_declined" ||
    actionType === "community.role_accepted" ||
    // DEV-1193 Phase 4: slot-anchored community role invitations
    // (parallel to the V1 community.role_assignment action types above).
    actionType === "community.slot_role_assignment" ||
    actionType === "community.slot_role_accepted" ||
    actionType === "community.slot_role_declined" ||
    actionType === "community.slot_role_resignation"
  ) {
    return "people";
  }

  if (
    actionType === "content.publish_to_core_element" ||
    actionType === "campaign.qualify_project" ||
    actionType === "project.role_completion_verification" ||
    actionType === "content.domain_blacklist_appeal"
  )
    return "reviews";

  if (
    actionType === "project.ownership_transfer" ||
    actionType === "community.ownership_transfer" ||
    actionType === "community.pm_trust_promotion" ||
    actionType === "impact.admin_grant"
  )
    return "governance";

  if (actionType === "project.kpi_progress_update") return "data";

  return "notifications";
}

/* ── Avatar helper ───────────────────────────────────────────────── */

function resolveAvatarUrl(user: Record<string, any> | undefined): string | null {
  if (!user) return null;
  // Delegates to the shared tolerant resolver so every stored format works.
  return mediaSrc(user.profileImageUrl || user.profile_image_url || "");
}

/* ── GUID → display name + avatar resolver ───────────────────────── */

interface ResolvedUser {
  name: string;
  avatarUrl: string | null;
}

function buildUserLookup(
  items: Record<string, any>[] | undefined,
): Map<string, ResolvedUser> {
  const map = new Map<string, ResolvedUser>();
  for (const item of items || []) {
    const guid = String(
      item.user_guid || item.userGuid || item.guid || "",
    ).toUpperCase();
    if (!guid) continue;
    const name = String(
      item.display_name || item.displayName || item.name || item.email || "",
    );
    map.set(guid, { name, avatarUrl: resolveAvatarUrl(item) });
  }
  return map;
}

function buildEntityLookup(
  items: Record<string, any>[] | undefined,
  guidKeys: string[],
  nameKeys: string[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items || []) {
    let guid = "";
    for (const k of guidKeys) {
      guid = String(item[k] || "");
      if (guid) break;
    }
    if (!guid) continue;
    let name = "";
    for (const k of nameKeys) {
      name = String(item[k] || "");
      if (name) break;
    }
    if (name) map.set(guid.toUpperCase(), name);
  }
  return map;
}

/* ── Initials from name ──────────────────────────────────────────── */

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/* ── Relative time formatting ────────────────────────────────────── */

function relativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString();
}

/* ── Status helpers ──────────────────────────────────────────────── */

function isCompleted(w: WorkflowRequest): boolean {
  // Terminal states — the workflow no longer awaits action.
  // 'approved'    — accepted; relationship visible elsewhere (Engagements/Lead).
  // 'resigned'    — recipient self-removed after acceptance (DEV-1028).
  // 'revoked'     — granter ended the relationship after acceptance (DEV-1028).
  // 'acknowledged' — DEV-1030: dismiss state for resign/revoke/decline
  //                  notifications. Distinct from 'cancelled' so audit can
  //                  tell "user saw the notice" from "user backed out."
  return (
    w.status === "approved"
    || w.status === "rejected"
    || w.status === "cancelled"
    || w.status === "rescinded"
    || w.status === "resigned"
    || w.status === "revoked"
    || w.status === "acknowledged"
  );
}

/**
 * DEV-1030 — Pure-notification action types. The recipient has nothing to
 * approve / reject; the underlying event already happened. The inbox row
 * exists only so the badge alerts them, and a single Acknowledge button
 * dismisses it (transitions to 'acknowledged'). Distinct from
 * isRoleInvitation which still uses Accept/Reject because the underlying
 * decision is not yet made.
 */
function isAcknowledgmentNotification(actionType: string): boolean {
  return (
    actionType === "community.role_resignation"
    || actionType === "community.role_revoked"
    || actionType === "community.role_declined"
    || actionType === "community.role_accepted"
    || actionType === "project.role_resignation"
    || actionType === "project.role_revoked"
    || actionType === "project.role_declined"
    || actionType === "project.role_accepted"
    || actionType === "mini_project.role_accepted"
    || actionType === "mini_project.role_declined"
    || actionType === "mini_project.role_resignation"
    || actionType === "mini_project.role_revoked"
    // DEV-1193 follow-up: slot-anchored community invitation acks
    // collapse to a single Acknowledge button — same as the project
    // and V1 community ack types above. Without this, the inviter
    // sees an Accept/Decline pair on RISSAR's acceptance ack, which
    // is confusing — there's nothing for the inviter to approve.
    || actionType === "community.slot_role_accepted"
    || actionType === "community.slot_role_declined"
    || actionType === "community.slot_role_resignation"
  );
}

function statusColor(status: string): string {
  switch (status) {
    case "submitted":
    case "needs_acceptance":
      return "var(--color-warning, #f59e0b)";
    case "approved":
      return "var(--color-success, #22c55e)";
    case "rejected":
    case "cancelled":
      return "var(--color-error, #ef4444)";
    case "rescinded":
      return "var(--text-tertiary)";
    default:
      return "var(--text-tertiary)";
  }
}

/* ── Category icon SVGs ──────────────────────────────────────────── */

/** Icon for "invitation" sub-type: user-plus (we're inviting you) */
const IconInvitation: React.FC<{ size?: number }> = ({ size = 14 }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>;
};

/** Icon for "access" sub-type: key (they want in) */
const IconAccess: React.FC<{ size?: number }> = ({ size = 14 }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return <svg {...common}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>;
};

const CategoryIcon: React.FC<{ category: ItemCategory; peopleSubType?: PeopleSubType }> = ({ category, peopleSubType }) => {
  const size = 14;
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (category) {
    case "people":
      return peopleSubType === "access" ? <IconAccess size={size} /> : <IconInvitation size={size} />;
    case "reviews":
      return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
    case "governance":
      return <svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case "data":
      return <svg {...common}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
    case "notifications":
      return <svg {...common}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
    default:
      return null;
  }
};

/* ══════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                   */
/* ══════════════════════════════════════════════════════════════════ */

const ContentApprovalsPage: React.FC<Props> = ({
  currentUserGuid,
  debugMode,
  users,
  projects,
  communities,
  relationships,
  data,
  onProjectConfigChanged,
  communityMemberships,
  projectAcl,
  allProjectHeroLookup,
  communityRoleAssignments,
}) => {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [version, setVersion] = useState(0);

  /*
    DEV-681 Phase 1: Action Center organized into 4 primary tabs in
    user-facing order (left → right): Tasks · Data · Approvals · Tracking.
      - tasks:     user progression milestones — what's most personal
      - data:      KPI data entries due
      - approvals: workflow inbox (all categories EXCEPT data)
      - tracking:  workflow outbox (items I sent waiting on others)

    The leftmost tab (Tasks) is the default landing tab. Order matches
    Skip's product intent: the most personal/foundational items first,
    moving outward toward items involving others.

    The legacy ?filter= deep-link still resolves to the closest equivalent
    tab so any external links (e.g. Governance shortcut from LeftNav) keep
    working: people/reviews/governance/notifications → approvals; data → data.
  */
  type ActionTab = 'tasks' | 'data' | 'approvals' | 'tracking';

  const initialTab = useMemo<ActionTab>(() => {
    const f = searchParams.get('filter');
    if (f === 'data') return 'data';
    if (f === 'people' || f === 'reviews' || f === 'governance' || f === 'notifications') {
      return 'approvals';
    }
    return 'tasks';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- read once on mount

  const [activeTab, setActiveTab] = useState<ActionTab>(initialTab);
  const [showApprovalsCompleted, setShowApprovalsCompleted] = useState(false);
  const [showTrackingCompleted, setShowTrackingCompleted] = useState(false);
  const [expandedGuid, setExpandedGuid] = useState<string | null>(null);
  // DEV-1089 Phase A. Per-row draft reply-note text for invitation rows.
  // Keyed by workflow_guid so the textarea persists if the user clicks
  // away and back, but is cleared when a different invitation expands.
  // The note is captured at action time (Accept or Decline) and flows
  // into the workflow history + dispatcher.
  const [replyNotes, setReplyNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    return subscribeWorkflows(() => setVersion((v) => v + 1));
  }, []);

  /* ── Lookup maps ── */
  const userLookup = useMemo(() => buildUserLookup(users), [users]);
  const projectMap = useMemo(
    () =>
      buildEntityLookup(
        projects,
        ["project_GUID", "project_guid", "projectGuid", "guid"],
        ["project_name", "projectName", "name"],
      ),
    [projects],
  );
  const communityMap = useMemo(
    () =>
      buildEntityLookup(
        communities,
        ["entity_guid", "entityGuid", "guid"],
        ["name"],
      ),
    [communities],
  );

  const resolveUser = useCallback(
    (guid: string | undefined | null): ResolvedUser => {
      if (!guid) return { name: "", avatarUrl: null };
      return userLookup.get(String(guid).toUpperCase()) || { name: String(guid).slice(0, 8), avatarUrl: null };
    },
    [userLookup],
  );

  const resolveName = useCallback(
    (guid: string | undefined | null): string => {
      if (!guid) return "";
      const upper = String(guid).toUpperCase();
      return (
        userLookup.get(upper)?.name ||
        projectMap.get(upper) ||
        communityMap.get(upper) ||
        ""
      );
    },
    [userLookup, projectMap, communityMap],
  );

  /**
   * DEV-892. Disambiguating community label. When the entity is org-typed
   * (entity_type starts with "org_"), walk the ancestor chain to the org
   * root and append it in parens. Resolves the "Pawtucket geo_city vs
   * Pawtucket org_rotary_club" ambiguity in role invitations.
   *
   * Examples:
   *   geo_city Pawtucket          → "Pawtucket"
   *   org_rotary_club Pawtucket   → "Pawtucket Rotary Club (Rotary International)"
   *   organization Rotary Intl    → "Rotary International"
   */
  const resolveCommunityLabel = useCallback(
    (guid: string | undefined | null): string => {
      if (!guid) return "";
      const upper = String(guid).toUpperCase();
      // Build a guid→entity map for this resolver only — index by uppercase guid
      // so casing doesn't matter. Not memoized separately because communityMap is.
      const ents = (communities || []) as any[];
      const byGuid = new Map<string, any>();
      for (const e of ents) {
        const g = String(e.entity_guid || e.entityGuid || e.guid || "").toUpperCase();
        if (g) byGuid.set(g, e);
      }
      const me = byGuid.get(upper);
      if (!me) return formatCommunityPathLabel(guid) || resolveName(guid);
      const myType = String(me.entity_type || me.entityType || "");
      const myName = String(me.name || "");
      // DEV-1025 — geo entities get the full path label (leaf-first, comma-
      // separated) so "Newport" disambiguates as "Newport, Rhode Island,
      // United States". The path resolver walks parent_of / contains edges,
      // strips Earth, and caps at 3 levels.
      if (!myType.startsWith("org_") && myType !== "organization") {
        const pathLabel = formatCommunityPathLabel(guid);
        if (pathLabel) return pathLabel;
        return myName || resolveName(guid);
      }
      // Walk to the org root via relationships
      const rels = (relationships || []) as any[];
      const parentByChild = new Map<string, string>();
      for (const r of rels) {
        const t = String(r.relationship_type || r.relationshipType || "");
        if (t !== "parent_of" && t !== "contains") continue;
        const c = String(r.child_entity_guid || r.childEntityGuid || "").toUpperCase();
        const p = String(r.parent_entity_guid || r.parentEntityGuid || "").toUpperCase();
        if (c && p) parentByChild.set(c, p);
      }
      let cur = upper;
      let safety = 0;
      while (parentByChild.has(cur) && safety++ < 12) {
        cur = parentByChild.get(cur)!;
      }
      const root = byGuid.get(cur);
      const rootName = String(root?.name || "");
      // If we walked to ourselves (entity is its own root) or the root is the
      // same as me, just return the bare name.
      if (!rootName || cur === upper) return myName;
      return `${myName} (${rootName})`;
    },
    [communities, relationships, resolveName],
  );

  /** Build a human-readable, localized description of a workflow request. */
  const describeRequest = useCallback(
    (w: WorkflowRequest): string => {
      const p = w.payload || {};
      const action = w.action_type;
      const a = (key: string, vars?: Record<string, string>) =>
        t(`contentApprovals.actions.${key}`, vars || {});

      const userName =
        resolveName(p.user_guid) || resolveName(p.target_user_guid);
      const targetName = resolveName(w.target_guid);
      const role = p.requested_role || p.role || "";
      const subjectGuid = String(p.user_guid || p.target_user_guid || "").toUpperCase();
      const createdBy = String(w.created_by || "").toUpperCase();

      if (action === "project.role_assignment") {
        const proj =
          resolveName(p.project_guid) ||
          targetName ||
          t("contentApprovals.aProject");
        // Access request: person requesting for themselves
        const isSelfRequest = subjectGuid && createdBy && subjectGuid.toUpperCase() === createdBy.toUpperCase();
        if (isSelfRequest && role)
          return a("requestAccessRoleProject", { user: userName, role, target: proj });
        if (isSelfRequest)
          return a("requestAccessProject", { user: userName, target: proj });
        if (userName && role)
          return a("assignUserRoleProject", { user: userName, role, target: proj });
        if (userName)
          return a("assignUserProject", { user: userName, target: proj });
        return a("roleAssignmentProject", { target: proj });
      }

      // DEV-1088. Mini-Project role invitations. No self-request flavor —
      // Mini-Project Phase 1 is Lead-initiated only. Reuses the existing
      // "assignUserRoleProject" / "assignUserProject" / "roleAssignmentProject"
      // strings; project name resolution uses mini_project_guid from the
      // payload, falling through to targetName (workflow.target_guid =
      // mini_project_guid by emitter convention in MiniProjectRolesController).
      if (action === "mini_project.role_assignment") {
        const proj =
          resolveName(p.mini_project_guid) ||
          targetName ||
          t("contentApprovals.aProject");
        if (userName && role)
          return a("assignUserRoleProject", { user: userName, role, target: proj });
        if (userName)
          return a("assignUserProject", { user: userName, target: proj });
        return a("roleAssignmentProject", { target: proj });
      }

      if (action === "project.permissions_change") {
        const proj =
          resolveName(p.project_guid) ||
          targetName ||
          t("contentApprovals.aProject");
        // Access request: person requesting for themselves
        const isSelfRequest = subjectGuid && createdBy && subjectGuid.toUpperCase() === createdBy.toUpperCase();

        // DEV-936: surface the actual change so the approver isn't blind.
        // Payload carries operation + old_role + new_role; render as a
        // suffix on the base description.
        const subjectName = resolveName(p.target_user_guid) || resolveName(p.user_guid) || "";
        const op = String(p.operation || "set");
        const oldRole = String(p.old_role || "").trim();
        const newRole = String(p.new_role || "").trim();
        let detail = "";
        if (subjectName) {
          if (op === "remove") {
            detail = ` — ${a("changeDetailRemove", { user: subjectName })}`;
          } else if (oldRole && newRole && oldRole !== newRole) {
            detail = ` — ${a("changeDetailRoleSwap", { user: subjectName, oldRole, newRole })}`;
          } else if (newRole) {
            detail = ` — ${a("changeDetailRoleSet", { user: subjectName, role: newRole })}`;
          }
        }

        if (isSelfRequest)
          return a("requestAccessProject", { user: userName, target: proj }) + detail;
        if (userName)
          return a("changePermissionsUser", { user: userName, target: proj }) + detail;
        return a("permissionsChange", { target: proj }) + detail;
      }

      if (action === "project.ownership_transfer") {
        const proj =
          resolveName(p.project_guid) ||
          targetName ||
          t("contentApprovals.aProject");
        const newOwner = resolveName(p.new_owner_guid) || userName || "";
        if (newOwner)
          return a("transferOwnershipTo", { target: proj, newOwner });
        return a("ownershipTransfer", { target: proj });
      }

      if (action === "project.kpi_progress_update") {
        const proj =
          resolveName(p.project_guid) ||
          targetName ||
          t("contentApprovals.aProject");
        return a("kpiProgressUpdate", { target: proj });
      }

      if (action === "community.role_assignment") {
        // DEV-892: use the disambiguating label so org-typed communities show
        // their org root in parens, e.g. "Pawtucket Rotary Club (Rotary International)"
        const comm =
          resolveCommunityLabel(p.community_guid || p.entity_guid) ||
          resolveName(p.community_guid || p.entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        const isSelfRequest = subjectGuid && createdBy && subjectGuid === createdBy;
        if (isSelfRequest && role)
          return a("requestAccessRoleCommunity", { user: userName, role, target: comm });
        if (isSelfRequest)
          return a("requestAccessCommunity", { user: userName, target: comm });
        if (userName && role)
          return a("assignUserRoleCommunity", { user: userName, role, target: comm });
        if (userName)
          return a("assignUserCommunity", { user: userName, target: comm });
        return a("roleAssignmentCommunity", { target: comm });
      }

      // DEV-1193 Phase 4: slot-anchored community role invitation.
      // Always admin-initiated (no self-request flavor — the only path
      // is the SlotEditModal Create-Invitation flow). Payload carries
      // role_label and community_entity_guid.
      // Wording uses "Invite" not "Assign" — the invitee hasn't
      // accepted yet, so it's an invitation, not an assignment.
      if (action === "community.slot_role_assignment") {
        const comm =
          resolveCommunityLabel(p.community_entity_guid) ||
          resolveName(p.community_entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        const slotRole = String(p.role_label || "").trim();
        if (userName && slotRole)
          return a("inviteUserRoleCommunity", { user: userName, role: slotRole, target: comm });
        if (userName)
          return a("inviteUserCommunity", { user: userName, target: comm });
        return a("roleInvitationCommunity", { target: comm });
      }

      if (action === "community.permissions_change") {
        const comm =
          resolveCommunityLabel(p.community_guid || p.entity_guid) ||
          resolveName(p.community_guid || p.entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        const isSelfRequest = subjectGuid && createdBy && subjectGuid === createdBy;

        // DEV-936: surface the actual change so the approver isn't blind.
        // Payload carries operation + old/new_role + old/new_is_member.
        const subjectName = resolveName(p.target_user_guid) || resolveName(p.user_guid) || "";
        const op = String(p.operation || "set");
        const oldRole = String(p.old_role || "").trim();
        const newRole = String(p.new_role || "").trim();
        const oldMember = (p as any).old_is_member;
        const newMember = (p as any).new_is_member;
        let detail = "";
        if (subjectName) {
          if (op === "remove") {
            detail = ` — ${a("changeDetailRemove", { user: subjectName })}`;
          } else if (oldRole && newRole && oldRole !== newRole) {
            detail = ` — ${a("changeDetailRoleSwap", { user: subjectName, oldRole, newRole })}`;
          } else if (newRole) {
            detail = ` — ${a("changeDetailRoleSet", { user: subjectName, role: newRole })}`;
          }
          if (typeof newMember === "boolean" && oldMember !== newMember) {
            const memKey = newMember ? "changeDetailJoinMember" : "changeDetailRemoveMember";
            detail += detail ? `; ${a(memKey)}` : ` — ${a(memKey)}`;
          }
        }

        if (isSelfRequest)
          return a("requestAccessCommunity", { user: userName, target: comm }) + detail;
        if (userName)
          return a("changePermissionsCommunity", { user: userName, target: comm }) + detail;
        return a("permissionsChangeCommunity", { target: comm }) + detail;
      }

      if (action === "community.ownership_transfer") {
        const comm =
          resolveCommunityLabel(p.community_guid || p.entity_guid) ||
          resolveName(p.community_guid || p.entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        const newOwner = resolveName(p.new_owner_guid) || userName || "";
        if (newOwner)
          return a("transferOwnershipCommunity", { target: comm, newOwner });
        return a("ownershipTransferCommunity", { target: comm });
      }

      if (action === "content.publish_to_core_element") {
        const proj =
          resolveName(p.project_guid) ||
          targetName ||
          t("contentApprovals.aProject");
        return a("publishContent", { target: proj });
      }

      if (action === "campaign.qualify_project") {
        const proj =
          resolveName(p.project_guid) ||
          targetName ||
          t("contentApprovals.aProject");
        return a("qualifyForCampaign", { target: proj });
      }

      // DEV-1024 — Lifecycle acknowledgment notifications (DEV-1030/1031).
      // Backend (PermissionsController.AcceptGrant / DeclineGrant /
      // RevokeGrant / ResignGrant) writes these workflow rows when a grant
      // changes state post-invitation. Payload always carries community_guid
      // (or project_guid) + user_guid (the subject — the person whose grant
      // moved). The role/bundle name is NOT in the payload, so labels phrase
      // around "a role" rather than naming it. Without these branches the
      // Inbox falls through to genericTarget and shows raw action_type.
      if (action === "community.role_accepted") {
        const comm =
          resolveCommunityLabel(p.community_guid || p.entity_guid) ||
          resolveName(p.community_guid || p.entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        if (userName) return a("roleAcceptedCommunity", { user: userName, target: comm });
        return a("roleAcceptedCommunityNoUser", { target: comm });
      }

      if (action === "community.role_declined") {
        const comm =
          resolveCommunityLabel(p.community_guid || p.entity_guid) ||
          resolveName(p.community_guid || p.entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        if (userName) return a("roleDeclinedCommunity", { user: userName, target: comm });
        return a("roleDeclinedCommunityNoUser", { target: comm });
      }

      if (action === "community.role_revoked") {
        const comm =
          resolveCommunityLabel(p.community_guid || p.entity_guid) ||
          resolveName(p.community_guid || p.entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        if (userName) return a("roleRevokedCommunity", { user: userName, target: comm });
        return a("roleRevokedCommunityNoUser", { target: comm });
      }

      if (action === "community.role_resignation") {
        const comm =
          resolveCommunityLabel(p.community_guid || p.entity_guid) ||
          resolveName(p.community_guid || p.entity_guid) ||
          targetName ||
          t("contentApprovals.aCommunity");
        if (userName) return a("roleResignedCommunity", { user: userName, target: comm });
        return a("roleResignedCommunityNoUser", { target: comm });
      }

      if (action === "project.role_accepted") {
        const proj =
          resolveName(p.project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleAcceptedProject", { user: userName, target: proj });
        return a("roleAcceptedProjectNoUser", { target: proj });
      }

      if (action === "project.role_declined") {
        const proj =
          resolveName(p.project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleDeclinedProject", { user: userName, target: proj });
        return a("roleDeclinedProjectNoUser", { target: proj });
      }

      // DEV-1088. Mini-Project ack rows reuse the project copy keys —
      // wording is identical from the inviter's perspective. The
      // distinguishing context is the project name, resolved from the
      // mini_project_guid payload field.
      if (action === "mini_project.role_accepted") {
        const proj =
          resolveName(p.mini_project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleAcceptedProject", { user: userName, target: proj });
        return a("roleAcceptedProjectNoUser", { target: proj });
      }

      if (action === "mini_project.role_declined") {
        const proj =
          resolveName(p.mini_project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleDeclinedProject", { user: userName, target: proj });
        return a("roleDeclinedProjectNoUser", { target: proj });
      }

      // DEV-1091. Mini-Project self-resignation ack to the inviter.
      // Reuses the project resignation copy keys — wording is
      // identical from the inviter's perspective. Project name comes
      // from the mini_project_guid payload field.
      if (action === "mini_project.role_resignation") {
        const proj =
          resolveName(p.mini_project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleResignedProject", { user: userName, target: proj });
        return a("roleResignedProjectNoUser", { target: proj });
      }

      // DEV-1090. Mini-Project role revoked via team-removal cascade.
      // From the affected user's perspective: "Your role was ended on
      // {project} because you were removed from the team." Project name
      // comes from the mini_project_guid payload field. Reuses the
      // project revoke copy keys.
      if (action === "mini_project.role_revoked") {
        const proj =
          resolveName(p.mini_project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleRevokedProject", { user: userName, target: proj });
        return a("roleRevokedProjectNoUser", { target: proj });
      }

      if (action === "project.role_revoked") {
        const proj =
          resolveName(p.project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleRevokedProject", { user: userName, target: proj });
        return a("roleRevokedProjectNoUser", { target: proj });
      }

      if (action === "project.role_resignation") {
        const proj =
          resolveName(p.project_guid) || targetName || t("contentApprovals.aProject");
        if (userName) return a("roleResignedProject", { user: userName, target: proj });
        return a("roleResignedProjectNoUser", { target: proj });
      }

      // Generic fallback
      const createdByName = resolveName(w.created_by);
      if (targetName)
        return a("genericTarget", { action, target: targetName });
      if (createdByName)
        return a("genericBy", { action, user: createdByName });
      return action;
    },
    [resolveName, resolveCommunityLabel, t],
  );

  /* ── Workflow lists ── */
  const all = useMemo(() => {
    void version; // ensure memo recomputes
    return getAllWorkflows();
  }, [version]);

  /** Inbox: items assigned to me (case-insensitive GUID comparison). */
  const inboxAll = useMemo(() => {
    const u = String(currentUserGuid || "").toUpperCase();
    if (!u) return [] as WorkflowRequest[];
    return all
      .filter((w) =>
        (w.assigned_to_guids || w.assigned_to_user_guids || [])
          .map((g) => String(g).toUpperCase())
          .includes(u),
      )
      .sort((a, b) =>
        String(b.updated_at || b.created_at).localeCompare(
          String(a.updated_at || a.created_at),
        ),
      );
  }, [all, currentUserGuid]);

  /** Outbox: items I created or submitted (case-insensitive GUID comparison). */
  const outboxAll = useMemo(() => {
    const u = String(currentUserGuid || "").toUpperCase();
    if (!u) return [] as WorkflowRequest[];
    return all
      .filter(
        (w) =>
          String(w.created_by).toUpperCase() === u ||
          String(w.submitted_by).toUpperCase() === u,
      )
      .sort((a, b) =>
        String(b.updated_at || b.created_at).localeCompare(
          String(a.updated_at || a.created_at),
        ),
      );
  }, [all, currentUserGuid]);

  /*
    DEV-681 Phase 1: tabs replace the category-filter chip bar.
    - Approvals tab: everything in the inbox except `data` category (which
      now lives in its own Data tab).
    - Tracking tab: existing outbox semantics (items I created/submitted).
    - The completed-items toggle is preserved per-tab since resolved items
      are still useful for audit/history.

    `data` items in the outbox (e.g. KPI updates I've submitted that are
    waiting on review) stay in Tracking — Tracking is "what's waiting on
    others", regardless of category.
  */
  const isData = useCallback(
    (w: WorkflowRequest) => categorize(w.action_type, w) === 'data',
    [],
  );

  const approvalsFiltered = useMemo(
    () =>
      inboxAll.filter((w) => {
        if (isData(w)) return false; // data items live in the Data tab
        if (!showApprovalsCompleted && isCompleted(w)) return false;
        return true;
      }),
    [inboxAll, isData, showApprovalsCompleted],
  );

  const trackingFiltered = useMemo(
    () =>
      outboxAll.filter((w) => {
        if (!showTrackingCompleted && isCompleted(w)) return false;
        return true;
      }),
    [outboxAll, showTrackingCompleted],
  );

  // ── Counts for the four tab badges + page-level summary ──
  const approvalsActiveCount = useMemo(
    () => inboxAll.filter((w) => !isData(w) && !isCompleted(w)).length,
    [inboxAll, isData],
  );
  /*
    DEV-681 Phase 2 — the Data tab badge now reflects actionable KPI
    instances (due + overdue) across all visible project rows, NOT
    legacy `project.kpi_progress_update` workflow items. The real
    signal lives in each row's `data_reliability_expected_instances`
    paired with the shared `classifyInstanceLateness` selector. We
    classify here too rather than relying on the page count flowing
    in via prop, so the tab badge updates with the same cadence as
    the rest of the engine output.
  */
  const dataActiveCount = useMemo(() => {
    if (!data || data.length === 0) return 0;
    const today = new Date().toISOString().slice(0, 10);
    let total = 0;
    for (const row of data) {
      if (!row?.action_center_can_edit_kpi) continue;
      // Action Center only counts data obligations for actively-executing
      // projects (project_status === 'In Progress'). Same semantic as the
      // rendered list — keeps badge and content in lockstep.
      if (!row?._isActiveProject) continue;
      const expected = row?.data_reliability_expected_instances || [];
      for (const inst of expected) {
        const state = classifyInstanceLateness({
          instance: inst,
          asOfDate: today,
          defaultGraceDays: 2,
        });
        if (state === 'due' || state === 'overdue') total += 1;
      }
    }
    return total;
  }, [data]);
  const trackingActiveCount = useMemo(
    () => outboxAll.filter((w) => !isCompleted(w)).length,
    [outboxAll],
  );
  /*
    DEV-681 Phase 3 — Tasks badge count.
    Counts incomplete `mandatory` + `recommended` milestones for the
    user's archetype (synthetic evaluator path C). Optional milestones
    don't ping the bell — they're nice-to-haves, not nags.
    Archetype defaults to 'explorer' for users without a stored
    archetype yet (matches TasksTab's resolution).
  */
  const tasksActiveCount = useMemo(() => {
    if (!currentUserGuid) return 0;
    const guid = currentUserGuid.toUpperCase();
    const currentUser = (users || []).find(
      (u: any) => String(u?.user_guid || '').toUpperCase() === guid,
    );
    const achieved = evaluateMilestones({
      currentUserGuid,
      currentUser,
      communityMemberships,
      projectAcl,
      projects,
    });
    const archetypeConfig = getArchetypeConfig('explorer');
    const incomplete = listIncompleteMilestones(archetypeConfig, achieved);
    return incomplete.filter(
      (m) => m.severity === 'mandatory' || m.severity === 'recommended',
    ).length;
  }, [currentUserGuid, users, communityMemberships, projectAcl, projects]);

  const canAct = !!currentUserGuid;

  /* ── Actions ── */
  const onApprove = (w: WorkflowRequest) => {
    if (!canAct) return;
    // DEV-1030 — acknowledgment-only notifications skip the side-effect
    // pipeline entirely. The Acknowledge button just marks the row as
    // 'acknowledged' so it falls out of the active inbox. The underlying
    // domain event (resignation, revoke, decline) already happened and
    // has no follow-up work attached.
    if (isAcknowledgmentNotification(w.action_type)) {
      const at = new Date().toISOString();
      const next: WorkflowRequest = {
        ...w,
        status: "acknowledged",
        history: [
          ...(w.history || []),
          { action: "acknowledge", by: String(currentUserGuid), at },
        ],
      };
      addOrUpdateWorkflow(next);
      return;
    }
    // Domain blacklist appeal: prompt user for reason before filing appeal
    if (w.action_type === "content.domain_blacklist_appeal") {
      const appealNote = window.prompt(t("contentApprovals.appealPrompt"), "");
      if (appealNote === null) return; // cancelled
      if (!appealNote.trim()) {
        window.alert(t("contentApprovals.appealNoteRequired"));
        return;
      }
      // Transition to approved = appeal filed with the note in history
      const next = approveRequest(w, String(currentUserGuid));
      next.history = [
        ...(next.history || []),
        { action: "appeal", by: String(currentUserGuid), at: new Date().toISOString(), note: appealNote.trim() },
      ];
      addOrUpdateWorkflow(next);
      // Also update the backend blocked_share_domains to set appeal_status = 'pending'
      const domain = (w.payload as any)?.domain;
      if (domain) {
        // Fire-and-forget — the local workflow is already updated
        fileBlockedDomainAppeal(domain, w.workflow_guid, appealNote.trim()).catch(() => {
          // The appeal note is recorded in the workflow; backend sync is best-effort
        });
      }
      return;
    }
    // DEV-1089 Phase A. Carry the invitee's inline reply-note into the
    // workflow history on accept. The dispatcher reads the most recent
    // history entry's note and forwards it to the role-invitation
    // respond endpoint, so the Lead sees the assignee's note in their
    // ack notification. Only invitations carry a reply-note; other
    // workflow types ignore the field.
    const isInvitationForNote =
      w.action_type === "project.role_assignment" ||
      w.action_type === "mini_project.role_assignment" ||
      w.action_type === "community.role_assignment" ||
      // DEV-1193 Phase 4: slot-anchored community invitation accepts a note too.
      w.action_type === "community.slot_role_assignment";
    const acceptNote = isInvitationForNote ? (replyNotes[w.workflow_guid] || "").trim() : "";

    const next = approveRequest(w, String(currentUserGuid));
    if (acceptNote) {
      // Stamp the note onto the terminal history entry so downstream
      // consumers (notification body, audit trail) have it. Match the
      // shape rejectRequest produces on the reject path.
      const lastIdx = (next.history || []).length - 1;
      if (lastIdx >= 0 && next.history) {
        next.history[lastIdx] = { ...next.history[lastIdx], note: acceptNote };
      }
    }
    const applied = applyApprovedWorkflowSideEffects(
      next,
      String(currentUserGuid),
    );
    if (!applied.ok) {
      window.alert(applied.error || t("contentApprovals.workflowError"));
      return;
    }
    addOrUpdateWorkflow(next);
    // Drop the draft note now that it's been committed.
    setReplyNotes((prev) => {
      if (!(w.workflow_guid in prev)) return prev;
      const next = { ...prev };
      delete next[w.workflow_guid];
      return next;
    });
  };

  const onReject = (w: WorkflowRequest) => {
    if (!canAct) return;
    const isDomainAppealItem = w.action_type === "content.domain_blacklist_appeal";
    const isInvitation =
      w.action_type === "project.role_assignment" ||
      w.action_type === "mini_project.role_assignment" ||
      w.action_type === "community.role_assignment" ||
      // DEV-1193 Phase 4: slot-anchored community invitation.
      w.action_type === "community.slot_role_assignment";
    // DEV-1089 Phase A. For invitations we now read the reply-note from
    // the inline textarea instead of the window.prompt. Other reject
    // paths (domain appeals, generic rejects) keep the prompt for now
    // because they don't render an expanded card with a textarea.
    let reason: string | null = null;
    if (isInvitation) {
      reason = (replyNotes[w.workflow_guid] || "").trim() || null;
    } else {
      const promptText = isDomainAppealItem
        ? t("contentApprovals.dismissPrompt")
        : t("contentApprovals.rejectPrompt");
      reason = window.prompt(promptText, "");
    }
    const next = rejectRequest(
      w,
      String(currentUserGuid),
      String(reason || ""),
    );
    applyRejectedWorkflowSideEffects(next, String(currentUserGuid));
    addOrUpdateWorkflow(next);
    if (isInvitation) {
      setReplyNotes((prev) => {
        if (!(w.workflow_guid in prev)) return prev;
        const next = { ...prev };
        delete next[w.workflow_guid];
        return next;
      });
    }
  };

  const onSubmit = (w: WorkflowRequest) => {
    if (!canAct) return;
    const next = submitRequest(w, String(currentUserGuid));
    addOrUpdateWorkflow(next);
  };

  const onCancel = (w: WorkflowRequest) => {
    if (!canAct) return;
    const next = cancelRequest(w, String(currentUserGuid));
    applyCancelledWorkflowSideEffects(next, String(currentUserGuid));
    addOrUpdateWorkflow(next);
  };

  const createDemo = () => {
    if (!canAct) return;
    const now = new Date();
    const guid = `wf_${now.getTime()}_${Math.random().toString(16).slice(2)}`;
    const demo: WorkflowRequest = {
      workflow_guid: guid,
      target_type: "project",
      target_guid: "DEMO_PROJECT_GUID",
      action_type: "project.role_assignment",
      payload: {
        project_guid: "DEMO_PROJECT_GUID",
        user_guid: "DEMO_USER_GUID",
        requested_role: "contributor",
      },
      status: "draft",
      version: 1,
      created_by: String(currentUserGuid),
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
      assigned_to_guids: [String(currentUserGuid)],
      history: [
        {
          action: "draft",
          by: String(currentUserGuid),
          at: now.toISOString(),
        },
      ],
      title: t("contentApprovals.actions.assignUserRoleProject", {
        user: "Demo User",
        role: "contributor",
        target: "Demo Project",
      }),
    };
    addOrUpdateWorkflow(demo);
  };

  /* ── Toggle card expansion ── */
  const toggleExpand = (guid: string) => {
    setExpandedGuid((prev) => (prev === guid ? null : guid));
  };

  /*
    DEV-681 Phase 1: filter chip bar dropped — tabs replace it.
    The `Show completed` toggle is preserved per-tab (Approvals + Tracking)
    since resolved items remain useful for audit/history and don't fit
    cleanly under any of the four tab labels.
  */
  const renderShowCompletedToggle = (
    showCompleted: boolean,
    setShowCompleted: (v: boolean) => void,
  ) => (
    <div className={styles.filterBar}>
      <label className={styles.completedToggle}>
        <input
          type="checkbox"
          checked={showCompleted}
          onChange={(e) => setShowCompleted(e.target.checked)}
        />
        {t("contentApprovals.showCompleted")}
      </label>
    </div>
  );

  /* ── Avatar component ── */
  const Avatar: React.FC<{ guid: string; size?: number }> = ({ guid, size = 32 }) => {
    const user = resolveUser(guid);
    const initials = getInitials(user.name);
    if (user.avatarUrl) {
      return (
        <img
          className={styles.avatar}
          src={user.avatarUrl}
          alt={user.name}
          style={{ width: size, height: size }}
        />
      );
    }
    return (
      <div
        className={styles.avatarFallback}
        style={{ width: size, height: size, fontSize: size * 0.4 }}
        title={user.name}
      >
        {initials}
      </div>
    );
  };

  /** Resolve the outcome line for completed outbox items (who acted + decision). */
  const resolveOutcome = useCallback(
    (w: WorkflowRequest): string | null => {
      if (!isCompleted(w)) return null;
      // Find the terminal history entry (approved/rejected/cancelled)
      const terminal = [...(w.history || [])].reverse().find(
        (h) => h.action === "approved" || h.action === "rejected" || h.action === "cancelled",
      );
      if (!terminal) return `${w.status}`;
      const actorName = resolveName(terminal.by) || terminal.by;
      const note = terminal.note ? ` — "${terminal.note}"` : "";
      // DEV-1088 follow-up. For role-invitation workflows, the
      // user-facing verb is Accepted / Declined / Cancelled — not
      // Approved / Rejected / Cancelled. The workflow library uses
      // generic approve/reject in its terminal history actions; we
      // map them to invitation-appropriate vocabulary here so the
      // Tracking row reads naturally.
      const isInvitation =
        w.action_type === "project.role_assignment" ||
        w.action_type === "mini_project.role_assignment" ||
        w.action_type === "community.role_assignment" ||
        // DEV-1193 Phase 4: slot-anchored community invitation.
        w.action_type === "community.slot_role_assignment";
      const verbKey = (() => {
        if (terminal.action === "approved") return isInvitation ? "outcomeAccepted" : "outcomeApproved";
        if (terminal.action === "rejected") return isInvitation ? "outcomeDeclined" : "outcomeRejected";
        return "outcomeCancelled";
      })();
      const verb = t(`contentApprovals.${verbKey}`);
      return `${verb} ${t("contentApprovals.outcomeBy")} ${actorName}${note}`;
    },
    [resolveName, t],
  );

  /* ── Render a single card row (email-inbox style) ── */
  const renderItem = (w: WorkflowRequest, isInbox: boolean) => {
    const description = describeRequest(w);
    const category = categorize(w.action_type, w);
    const peopleSubType = category === "people" ? getPeopleSubType(w) : undefined;
    const expanded = expandedGuid === w.workflow_guid;
    const completed = isCompleted(w);

    // Context-sensitive labels based on action type.
    // DEV-1021 / DEV-1025 — recipients of role invitations see Accept/Reject.
    // DEV-1030 — acknowledgment-only notifications (resignation/revoke/decline
    // notices) collapse to a single Acknowledge button. The underlying event
    // already happened and isn't reversible; the inbox row exists purely for
    // visibility, and a click transitions status to 'acknowledged'.
    const isRoleInvitation =
      w.action_type === "project.role_assignment" ||
      w.action_type === "community.role_assignment" ||
      w.action_type === "mini_project.role_assignment" ||
      // DEV-1193 Phase 4: slot-anchored community invitation gets the
      // same Accept / Decline UX as the other invitation types.
      w.action_type === "community.slot_role_assignment";
    const isDomainAppeal = w.action_type === "content.domain_blacklist_appeal";
    const isAckNotice = isAcknowledgmentNotification(w.action_type);
    const approveLabel = isDomainAppeal
      ? t("contentApprovals.appeal")
      : isAckNotice
        ? t("contentApprovals.acknowledge")
        : isRoleInvitation
          ? t("contentApprovals.accept")
          : t("contentApprovals.approve");
    const rejectLabel = isDomainAppeal
      ? t("contentApprovals.dismiss")
      : t("contentApprovals.reject");

    // DEV-1026 — Build the click-through link for role-related workflow rows.
    // Previously this only computed a project GUID, so community invitations
    // and the new DEV-1024 lifecycle rows (role_accepted / declined / revoked
    // / resignation) were either pointing at a non-existent project route or
    // 404'ing on `/communities/{guid}/alignment` (no matching <Route>). Pick
    // the right entity type from the workflow's action_type prefix, then
    // build the convention-matching href (?tab=about for projects, #alignment
    // for communities — communities use the hash because the tab is selected
    // by the destination page reading location.hash).
    const wfActionStr = String(w.action_type || "");
    const isCommunityWorkflow = wfActionStr.startsWith("community.");
    const isProjectWorkflow = wfActionStr.startsWith("project.");
    const linkEntityGuid = isCommunityWorkflow
      ? String(
          (w.payload as any)?.community_guid ||
            (w.payload as any)?.entity_guid ||
            w.target_guid ||
            "",
        )
      : isProjectWorkflow
        ? String(
            (w.payload as any)?.project_guid ||
              (w.payload as any)?.projectGuid ||
              w.target_guid ||
              "",
          )
        : "";
    const linkEntityHref = isCommunityWorkflow
      ? `/communities/${linkEntityGuid}#alignment`
      : `/projects/${linkEntityGuid}?tab=about`;
    const linkEntityFallback = isCommunityWorkflow
      ? t("contentApprovals.aCommunity")
      : t("contentApprovals.aProject");
    // Show the click-through link for any role-related workflow row —
    // invitations and the DEV-1024 acknowledgment notices alike. Recipients
    // benefit from "tap the name to see the place this is about" on both.
    const showEntityLink = isRoleInvitation || isAckNotice;

    // Resolve people
    const fromUser = resolveUser(w.created_by);
    const toGuids = w.assigned_to_guids || [];
    const firstToUser = toGuids.length > 0 ? resolveUser(toGuids[0]) : null;

    // For access requests in inbox, also resolve the person requesting access
    const subjectGuid = w.payload?.user_guid || w.payload?.target_user_guid;
    const isAccessRequest = category === "people" && peopleSubType === "access";

    // Outcome line for completed outbox items
    const outcomeLine = !isInbox && completed ? resolveOutcome(w) : null;

    return (
      <div
        key={w.workflow_guid}
        className={`${styles.row} ${expanded ? styles.rowExpanded : ""} ${completed ? styles.rowCompleted : ""}`}
        onClick={() => toggleExpand(w.workflow_guid)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggleExpand(w.workflow_guid);
        }}
      >
        {/* ── Compact summary row ── */}
        <div className={styles.rowSummary}>
          {/* From avatar + name */}
          <div className={styles.rowFrom}>
            {isInbox ? (
              <Avatar guid={isAccessRequest && subjectGuid ? subjectGuid : w.created_by} size={28} />
            ) : firstToUser ? (
              <Avatar guid={toGuids[0]} size={28} />
            ) : (
              <div className={styles.avatarFallback} style={{ width: 28, height: 28, fontSize: 11 }}>?</div>
            )}
            <span className={styles.rowName}>
              {isInbox ? fromUser.name || w.created_by : firstToUser?.name || (toGuids.length > 0 ? toGuids[0].slice(0, 8) : "—")}
              {!isInbox && toGuids.length > 1 && (
                <span className={styles.rowExtra}> +{toGuids.length - 1}</span>
              )}
            </span>
          </div>

          {/* Category icon + description */}
          <div className={styles.rowDescription}>
            <span className={styles.rowCategoryIcon} title={peopleSubType || category}>
              <CategoryIcon category={category} peopleSubType={peopleSubType} />
            </span>
            <span className={styles.rowDescText}>
              {description}
              {outcomeLine && (
                <span className={styles.rowOutcome}> · {outcomeLine}</span>
              )}
            </span>
            {linkEntityGuid && showEntityLink && (
              <a
                className={styles.projectLink}
                href={linkEntityHref}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(linkEntityHref);
                }}
              >
                {resolveName(linkEntityGuid) || linkEntityFallback} →
              </a>
            )}
          </div>

          {/* Status + timestamp */}
          <div className={styles.rowMeta}>
            <span
              className={styles.statusDot}
              style={{ background: statusColor(w.status) }}
              title={w.status}
            />
            <span className={styles.rowTime}>
              {relativeTime(w.updated_at || w.created_at)}
            </span>
          </div>
        </div>

        {/* ── Expanded detail panel ── */}
        {expanded && (
          <div className={styles.rowDetail} onClick={(e) => e.stopPropagation()}>
            <div className={styles.detailGrid}>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t("contentApprovals.status")}</span>
                <span className={styles.detailValue} style={{ color: statusColor(w.status) }}>
                  {w.status}
                </span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t("contentApprovals.type")}</span>
                <span className={styles.detailValue}>
                  {category === "people" && peopleSubType
                    ? t(`contentApprovals.category.${peopleSubType}`)
                    : t(`contentApprovals.category.${category}`)}
                </span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t("contentApprovals.from")}</span>
                <div className={styles.detailPerson}>
                  <Avatar guid={w.created_by} size={20} />
                  <span>{fromUser.name || w.created_by}</span>
                </div>
              </div>
              {toGuids.length > 0 && (
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>{t("contentApprovals.assignedTo")}</span>
                  <div className={styles.detailPersonList}>
                    {toGuids.map((g) => {
                      const u = resolveUser(g);
                      return (
                        <div key={g} className={styles.detailPerson}>
                          <Avatar guid={g} size={20} />
                          <span>{u.name || g.slice(0, 8)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t("contentApprovals.created")}</span>
                <span className={styles.detailValue}>
                  {new Date(w.created_at).toLocaleString()}
                </span>
              </div>
              {w.description && (
                <div className={`${styles.detailField} ${styles.detailFieldFull}`}>
                  <span className={styles.detailLabel}>{t("contentApprovals.details")}</span>
                  <span className={styles.detailValue}>{w.description}</span>
                </div>
              )}
              {/* Personal note from the inviter (role invitations) */}
              {isRoleInvitation && (w.payload as any)?.invitation_message && (
                <div className={`${styles.detailField} ${styles.detailFieldFull}`}>
                  <span className={styles.detailLabel}>{t("contentApprovals.personalNote")}</span>
                  <span className={styles.detailValue}>
                    &ldquo;{String((w.payload as any).invitation_message)}&rdquo;
                  </span>
                </div>
              )}
              {/* DEV-1050 follow-up — 2FA invitee warning. Producer (OA panel
                  for governance grants, ProjectRoleSlotsController for role-slot
                  invites) sets bundle_requires_2fa in the payload. Render an
                  unmistakable yellow banner so the invitee knows the role will
                  be dormant until they enroll TOTP. Generic copy works for
                  both "I haven't enabled 2FA" and "I have" cases — the role
                  activates automatically once enrolled. */}
              {isRoleInvitation && (w.payload as any)?.bundle_requires_2fa === true && (
                <div className={`${styles.detailField} ${styles.detailFieldFull}`} style={{
                  background: 'rgba(245, 158, 11, 0.10)',
                  borderInlineStart: '3px solid var(--status-warning, #f59e0b)',
                  padding: '8px 12px',
                  borderRadius: 6,
                }}>
                  <span className={styles.detailLabel}>{t("contentApprovals.twoFactorRequired", "Two-factor authentication required")}</span>
                  <span className={styles.detailValue}>
                    {t(
                      "contentApprovals.twoFactorWarning",
                      "This role grants elevated authority and requires 2FA. If you haven't enabled two-factor authentication, the role will be created but stay dormant — meaning it grants no capabilities — until you enable 2FA in your security settings."
                    )}
                  </span>
                </div>
              )}
              {/* Role description (role invitations) */}
              {isRoleInvitation && (w.payload as any)?.role_description && (
                <div className={`${styles.detailField} ${styles.detailFieldFull}`}>
                  <span className={styles.detailLabel}>{t("contentApprovals.roleDescription")}</span>
                  <span className={styles.detailValue}>
                    {String((w.payload as any).role_description)}
                  </span>
                </div>
              )}
              {/* DEV-1089 Phase B. Extended role spec — both Mini- and
                  Full-Project carry these in the payload now. We render
                  each block only when populated so older pending
                  invitations (created before the payload extension)
                  gracefully degrade. */}
              {isRoleInvitation && (w.payload as any)?.responsibilities && (
                <div className={`${styles.detailField} ${styles.detailFieldFull}`}>
                  <span className={styles.detailLabel}>
                    {t("contentApprovals.responsibilities")}
                  </span>
                  <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.55 }}>
                    {String((w.payload as any).responsibilities)
                      .split(/\r?\n/)
                      .map((line: string) => line.trim())
                      .filter((line: string) => line.length > 0)
                      .map((line: string, i: number) => (
                        <li key={i} style={{ fontSize: 13, marginBlockEnd: 2 }}>
                          {line}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {/* Skills — Mini-Project carries skills_desired (string,
                  newline-delimited), Full Project carries skills_required
                  (array). Normalize both into a chip row. */}
              {isRoleInvitation
                && (
                  (() => {
                    const p = w.payload as any;
                    const raw = p?.skills_desired ?? p?.skills_required;
                    if (raw == null) return false;
                    if (Array.isArray(raw)) return raw.length > 0;
                    if (typeof raw === "string") return raw.trim().length > 0;
                    return false;
                  })()
                ) && (
                <div className={`${styles.detailField} ${styles.detailFieldFull}`}>
                  <span className={styles.detailLabel}>
                    {t("contentApprovals.skills")}
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBlockStart: 4 }}>
                    {(() => {
                      const p = w.payload as any;
                      const raw = p?.skills_desired ?? p?.skills_required;
                      const list: string[] = Array.isArray(raw)
                        ? raw.map((s: string) => String(s).trim()).filter((s: string) => s.length > 0)
                        : String(raw || "")
                            .split(/\r?\n/)
                            .map((s: string) => s.trim())
                            .filter((s: string) => s.length > 0);
                      return list.map((s: string, i: number) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 12,
                            padding: "3px 10px",
                            background: "var(--bg-tertiary)",
                            borderRadius: 999,
                            border: "1px solid var(--border-default)",
                          }}
                        >
                          {s}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              )}
              {/* Time commitment — Full Project only */}
              {isRoleInvitation && (w.payload as any)?.time_commitment && (
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>
                    {t("contentApprovals.timeCommitment")}
                  </span>
                  <span className={styles.detailValue}>
                    {String((w.payload as any).time_commitment)}
                  </span>
                </div>
              )}
              {/* Verification level — Full Project only */}
              {isRoleInvitation && (w.payload as any)?.verification_level && (
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>
                    {t("contentApprovals.verificationLevel")}
                  </span>
                  <span className={styles.detailValue}>
                    {String((w.payload as any).verification_level)}
                  </span>
                </div>
              )}
              {/* Headcount — Mini-Project only, suppresses null/zero */}
              {isRoleInvitation
                && typeof (w.payload as any)?.headcount_needed === "number"
                && (w.payload as any).headcount_needed > 0 && (
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>
                    {t("contentApprovals.headcount")}
                  </span>
                  <span className={styles.detailValue}>
                    {(w.payload as any).headcount_needed}
                  </span>
                </div>
              )}
              {/* Domain blacklist appeal details */}
              {isDomainAppeal && (
                <>
                  <div className={styles.detailField}>
                    <span className={styles.detailLabel}>{t("contentApprovals.blockedDomain")}</span>
                    <span className={styles.detailValue} style={{ fontFamily: 'monospace' }}>
                      {String((w.payload as any)?.domain || '—')}
                    </span>
                  </div>
                  {(w.payload as any)?.articleTitle && (
                    <div className={styles.detailField}>
                      <span className={styles.detailLabel}>{t("contentApprovals.articleTitle")}</span>
                      <span className={styles.detailValue}>
                        {String((w.payload as any).articleTitle)}
                      </span>
                    </div>
                  )}
                  {(w.payload as any)?.articleUrl && (
                    <div className={`${styles.detailField} ${styles.detailFieldFull}`}>
                      <span className={styles.detailLabel}>{t("contentApprovals.articleUrl")}</span>
                      <span className={styles.detailValue} style={{ wordBreak: 'break-all' }}>
                        {String((w.payload as any).articleUrl)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* History / audit trail */}
            {w.history && w.history.length > 0 && (
              <div className={styles.historySection}>
                <span className={styles.detailLabel}>{t("contentApprovals.history")}</span>
                <div className={styles.historyList}>
                  {w.history.map((h, i) => (
                    <div key={i} className={styles.historyItem}>
                      <span className={styles.historyAction}>{h.action}</span>
                      <span className={styles.historyBy}>
                        {resolveName(h.by) || h.by}
                      </span>
                      <span className={styles.historyTime}>
                        {relativeTime(h.at)}
                      </span>
                      {h.note && (
                        <span className={styles.historyNote}>{h.note}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DEV-1089 Phase A. Inline reply-note textarea for
                invitation rows in the inbox, while the workflow is
                still pending. Replaces the prior window.prompt on
                Decline and adds an optional note on Accept (which
                the Mini-Project respond endpoint already accepts via
                the `note` field). Hidden for ack-only notifications,
                non-invitation rows, and completed/resolved rows. */}
            {isInbox
              && isRoleInvitation
              && !isAckNotice
              && w.status === "submitted"
              && (
                <div
                  className={`${styles.detailField} ${styles.detailFieldFull}`}
                  style={{ marginBlockStart: 4 }}
                >
                  <label
                    htmlFor={`reply-note-${w.workflow_guid}`}
                    className={styles.detailLabel}
                  >
                    {t("contentApprovals.replyNoteLabel")}
                  </label>
                  <textarea
                    id={`reply-note-${w.workflow_guid}`}
                    value={replyNotes[w.workflow_guid] || ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      const next = e.target.value;
                      setReplyNotes((prev) => ({ ...prev, [w.workflow_guid]: next }));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder={t("contentApprovals.replyNotePlaceholder")}
                    rows={3}
                    maxLength={2000}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid var(--border-default)",
                      background: "var(--bg-surface)",
                      color: "var(--text-primary)",
                      fontFamily: "inherit",
                      fontSize: 13,
                      resize: "vertical",
                      marginBlockStart: 4,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginBlockStart: 4,
                    }}
                  >
                    {t("contentApprovals.replyNoteHelp")}
                  </div>
                </div>
              )}

            {/* Action buttons */}
            <div className={styles.detailActions}>
              {isInbox ? (
                w.status === "submitted" ? (
                  isAckNotice ? (
                    /* DEV-1030 — acknowledgment-only notifications collapse
                       to a single primary button. There is no reject path;
                       the underlying event already happened. */
                    <button
                      className={styles.btnPrimary}
                      type="button"
                      onClick={() => onApprove(w)}
                    >
                      {approveLabel}
                    </button>
                  ) : (
                    <>
                      <button
                        className={styles.btnPrimary}
                        type="button"
                        onClick={() => onApprove(w)}
                      >
                        {approveLabel}
                      </button>
                      <button
                        className={styles.btnDanger}
                        type="button"
                        onClick={() => onReject(w)}
                      >
                        {rejectLabel}
                      </button>
                    </>
                  )
                ) : (
                  <div className={styles.mutedTiny}>
                    {completed
                      ? t("contentApprovals.resolved")
                      : t("contentApprovals.awaitingAcceptance")}
                  </div>
                )
              ) : w.status === "draft" ? (
                <>
                  <button
                    className={styles.btnPrimary}
                    type="button"
                    onClick={() => onSubmit(w)}
                  >
                    {t("contentApprovals.submit")}
                  </button>
                  <button
                    className={styles.btnSecondary}
                    type="button"
                    onClick={() => onCancel(w)}
                  >
                    {t("contentApprovals.cancel")}
                  </button>
                </>
              ) : (
                <div className={styles.mutedTiny}>
                  {completed
                    ? t("contentApprovals.resolved")
                    : t("contentApprovals.noActions")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ── Empty state ── */
  const EmptyState: React.FC<{ label: string }> = ({ label }) => (
    <div className={styles.emptyState}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
        <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
      </svg>
      <span>{label}</span>
    </div>
  );

  /*
    DEV-681 Phase 1: render the workflow row-list for either the
    Approvals tab (inbox semantics — items waiting on me) or the
    Tracking tab (outbox semantics — items I've sent that are waiting
    on others).
  */
  const renderWorkflowList = (
    panelType: 'inbox' | 'outbox',
    items: WorkflowRequest[],
    showCompleted: boolean,
    setShowCompleted: (v: boolean) => void,
  ) => (
    <div className={styles.panel}>
      {renderShowCompletedToggle(showCompleted, setShowCompleted)}
      <div className={styles.panelBody}>
        {items.length === 0 ? (
          <EmptyState label={t("contentApprovals.noItems")} />
        ) : (
          <div className={styles.list}>
            {items.map((w) => renderItem(w, panelType === 'inbox'))}
          </div>
        )}
      </div>
    </div>
  );

  /*
    DEV-681 Phase 1: placeholder content for Data + Tasks tabs. Phase 2
    composes inline MetricEntryGrid surfaces here; Phase 3 surfaces user
    progression milestones once DEV-500's user-facing pipeline ships.
  */
  const renderTabPlaceholder = (titleKey: string, hintKey: string) => (
    <div className={styles.panel}>
      <div className={styles.panelBody}>
        <div className={styles.emptyState}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span style={{ fontWeight: 600 }}>{t(titleKey)}</span>
          <span className={styles.mutedTiny} style={{ marginTop: 6 }}>
            {t(hintKey)}
          </span>
        </div>
      </div>
    </div>
  );

  /*
    DEV-1201 — single-Inbox Action Center (POLICY_ACTION_CENTER.md). Flag-gated
    so the four-tab IA stays the default during the parallel-run migration
    (§Q9). When enabled, the Inbox subsumes Tasks/Data/Approvals and Tracking
    moves to its own Sent view. EngagementsPage's Triage tab retirement is a
    later, separately-owned change and is NOT touched here.
  */
  if (isActionCenterInboxEnabled()) {
    return (
      /* DEV-1407 — .pageFlush, not .page: the Inbox owns a lane-level rail that
         must dock flush against LeftNav, so this wrapper contributes no padding
         and no scroll. The legacy four-tab branch below keeps .page. */
      <div className={styles.pageFlush}>
        <ActionCenterInbox
          currentUserGuid={currentUserGuid}
          debugMode={debugMode}
          users={users}
          projects={projects}
          communities={communities}
          relationships={relationships}
          data={data}
          onProjectConfigChanged={onProjectConfigChanged}
          communityMemberships={communityMemberships}
          projectAcl={projectAcl}
          allProjectHeroLookup={allProjectHeroLookup}
          communityRoleAssignments={communityRoleAssignments}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Page header — title is in the app bar, so only show the note when needed */}
      {!currentUserGuid && (
        <div className={styles.header}>
          <div className={styles.note}>{t("contentApprovals.selectUser")}</div>
        </div>
      )}

      {debugMode && (
        <div className={styles.debugRow}>
          <button
            className={styles.btnMini}
            type="button"
            onClick={createDemo}
            disabled={!currentUserGuid}
          >
            {t("contentApprovals.createDemo")}
          </button>
          <div className={styles.debugHint}>
            {t("contentApprovals.debugHint")}
          </div>
        </div>
      )}

      {/*
        DEV-681 Phase 1: page-level summary line gives the at-a-glance
        "what's waiting on me across all tabs" view without forcing a
        merged "All" list. Mirrors the bell badge's roll-up count.
      */}
      <div className={styles.pageSummary}>
        <span className={styles.pageSummaryItem}>
          {t('actionCenter.summary.tasks', { count: tasksActiveCount })}
        </span>
        <span className={styles.pageSummaryDivider}>·</span>
        <span className={styles.pageSummaryItem}>
          {t('actionCenter.summary.data', { count: dataActiveCount })}
        </span>
        <span className={styles.pageSummaryDivider}>·</span>
        <span className={styles.pageSummaryItem}>
          {t('actionCenter.summary.approvals', { count: approvalsActiveCount })}
        </span>
        <span className={styles.pageSummaryDivider}>·</span>
        <span className={styles.pageSummaryItem}>
          {t('actionCenter.summary.tracking', { count: trackingActiveCount })}
        </span>
      </div>

      {/*
        DEV-681 Phase 1: four-tab IA replaces the inbox/outbox + filter-bar
        layout. Approvals + Tracking carry forward the existing row chrome.
        Data + Tasks render placeholders until Phase 2 + Phase 3.
      */}
      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'tasks' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          {t('actionCenter.tabs.tasks')}
          {tasksActiveCount > 0 && (
            <span className={styles.tabBadge}>{tasksActiveCount}</span>
          )}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'data' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('data')}
        >
          {t('actionCenter.tabs.data')}
          {dataActiveCount > 0 && (
            <span className={styles.tabBadge}>{dataActiveCount}</span>
          )}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'approvals' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('approvals')}
        >
          {t('actionCenter.tabs.approvals')}
          {approvalsActiveCount > 0 && (
            <span className={styles.tabBadge}>{approvalsActiveCount}</span>
          )}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'tracking' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('tracking')}
        >
          {t('actionCenter.tabs.tracking')}
          {trackingActiveCount > 0 && (
            <span className={styles.tabBadge}>{trackingActiveCount}</span>
          )}
        </button>
      </div>

      {/* Active tab content */}
      <div className={styles.tabContent}>
        {activeTab === 'approvals' && renderWorkflowList(
          'inbox',
          approvalsFiltered,
          showApprovalsCompleted,
          setShowApprovalsCompleted,
        )}
        {activeTab === 'data' && (
          <DataTab data={data || []} onProjectConfigChanged={onProjectConfigChanged} />
        )}
        {activeTab === 'tasks' && (
          <TasksTab
            currentUserGuid={currentUserGuid}
            users={users}
            communityMemberships={communityMemberships}
            projectAcl={projectAcl}
            projects={projects}
          />
        )}
        {activeTab === 'tracking' && renderWorkflowList(
          'outbox',
          trackingFiltered,
          showTrackingCompleted,
          setShowTrackingCompleted,
        )}
      </div>
    </div>
  );
};

export default ContentApprovalsPage;
